import { DOCUMENT } from '@angular/common';
import { inject, Injectable, NgZone } from '@angular/core';

import { LoadingService } from './loading.service';

/**
 * One guard against double clicks for every button in an app, started once in each app's
 * `main.ts`. A second press of an action button used to start the same server action again
 * before the first had finished - 4Spaces picked five order lines twice from Multi Pick, 3-13 ms
 * apart (order 101012, 6 Oct 2026), and a transfer pallet got the same roll loaded twice.
 *
 * A clicked `button` (or `a.btn`, or a grid action button) is marked busy - greyed out, `aria-disabled` - and clicks on it
 * are swallowed until BOTH:
 * - `MinimumBusyMs` has passed, which covers a click whose work starts no request at all, and
 * - every HTTP request started since the click has finished, successfully or with an error,
 *   which covers a slow save that a time-based cooldown alone let through.
 * `MaximumBusyMs` releases it regardless. The click handlers are fire-and-forget, so completion
 * comes from the HTTP layer (`LoadingInterceptor`) and both limits are time-based: a button can
 * never be left dead after a failure.
 *
 * It greys out with a class rather than the `disabled` property, so it never fights a screen's
 * own `[disabled]` binding or Formly's `props.disabled`.
 *
 * Not covered: work started by something other than a click (a keyboard shortcut calling a
 * handler, a grid cell edit), and work chained behind a promise or timer that starts its request
 * after the button has been released.
 *
 * Every button is guarded by default, so nothing is added to an ordinary button. A button that
 * must accept rapid repeat clicks (a stepper, a zoom or page arrow) is an
 * `<app-repeat-click-button>`, which carries the opt-out, `data-repeat-click`, in its own
 * template - a screen never writes the attribute itself. The two other components that carry it
 * are each a single button already (the scanner keyboard button, the Ask Stan launcher).
 * Dropdown toggles, modal close buttons, the menu hamburger, accordion headers, AG Grid's own
 * controls, the date and time pickers and the rich-text toolbar are exempt already.
 */
@Injectable({
  providedIn: 'root',
})
export class ClickGuardService {
  static readonly BusyClass = 'click-guard-busy';
  static readonly MinimumBusyMs = 800;
  static readonly MaximumBusyMs = 120000;

  // a.grid-action-button is the AG Grid action-button renderer (ButtonRendererComponent), a link
  // with no .btn class.
  private static readonly GuardedSelector = 'button, a.btn, a.grid-action-button';
  // Open/close toggles that start no server work: the menu hamburger, accordion headers.
  private static readonly ExemptSelector =
    '.btn-close, .dropdown-toggle, [ngbDropdownToggle], [ngbDropdownAnchor], .navbar-toggler, .accordion-button';
  // On the button or any ancestor. The library widgets listed step through months, hours or
  // formatting and start no server work, so their buttons answer every click.
  private static readonly RepeatClickSelector = '[data-repeat-click], ngb-datepicker, ngb-timepicker, .ql-toolbar';
  private static readonly AgGridClassPrefix = 'ag-';

  private readonly document = inject(DOCUMENT);
  private readonly ngZone = inject(NgZone);
  private readonly loadingService = inject(LoadingService);

  // Each busy element and the function that releases it.
  private readonly busyElements = new Map<Element, () => void>();
  private started = false;

  private readonly onClick = (event: Event) => this.guardClick(event);

  /** Installs the document-level listener. Safe to call more than once. */
  start() {
    if (this.started) {
      return;
    }
    this.started = true;

    // Capture phase, so this runs before any (click) handler on the button itself.
    this.ngZone.runOutsideAngular(() => this.document.addEventListener('click', this.onClick, true));
  }

  /** Removes the listener and releases every busy element. */
  stop() {
    if (!this.started) {
      return;
    }
    this.started = false;
    this.document.removeEventListener('click', this.onClick, true);
    Array.from(this.busyElements.values()).forEach((release) => release());
  }

  isBusy(element: Element): boolean {
    return this.busyElements.has(element);
  }

  private guardClick(event: Event) {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    const element = target.closest(ClickGuardService.GuardedSelector);
    if (!element || this.isExempt(element)) {
      return;
    }

    if (this.busyElements.has(element)) {
      event.stopImmediatePropagation();
      event.preventDefault();
      return;
    }

    this.markBusy(element);
  }

  private isExempt(element: Element): boolean {
    return (
      !!element.closest(ClickGuardService.RepeatClickSelector) ||
      element.matches(ClickGuardService.ExemptSelector) ||
      Array.from(element.classList).some((className) => className.startsWith(ClickGuardService.AgGridClassPrefix))
    );
  }

  private markBusy(element: Element) {
    // Requests with a higher id than this were started by (or after) this click.
    const lastRequestBeforeClick = this.loadingService.lastRequestId;
    const previousAriaDisabled = element.getAttribute('aria-disabled');

    element.classList.add(ClickGuardService.BusyClass);
    element.setAttribute('aria-disabled', 'true');

    let minimumPassed = false;

    this.ngZone.runOutsideAngular(() => {
      const release = () => {
        clearTimeout(minimumTimer);
        clearTimeout(maximumTimer);
        settled.unsubscribe();
        this.busyElements.delete(element);
        element.classList.remove(ClickGuardService.BusyClass);
        // Put back what was there - unless the screen's own binding has written a new value
        // meanwhile (the action disabled its row), which must stand.
        if (element.getAttribute('aria-disabled') === 'true') {
          if (previousAriaDisabled === null) {
            element.removeAttribute('aria-disabled');
          } else {
            element.setAttribute('aria-disabled', previousAriaDisabled);
          }
        }
      };

      const releaseIfIdle = () => {
        if (this.busyElements.get(element) === release && minimumPassed) {
          if (!this.loadingService.hasPendingSince(lastRequestBeforeClick)) {
            release();
          }
        }
      };

      const minimumTimer = setTimeout(() => {
        minimumPassed = true;
        releaseIfIdle();
      }, ClickGuardService.MinimumBusyMs);
      const maximumTimer = setTimeout(release, ClickGuardService.MaximumBusyMs);

      // Checked a tick after each request finishes, so a request chained in the first one's
      // response handler has started (and keeps the button busy) before the check runs.
      const settled = this.loadingService.requestSettled$.subscribe(() =>
        this.ngZone.runOutsideAngular(() => setTimeout(releaseIfIdle)),
      );

      this.busyElements.set(element, release);
    });
  }
}
